import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const DehydratedAromatics = () => {
  const products = [
    {
      id: 'moringa',
      name: 'Moringa Leaf Powder',
      scientificName: 'Moringa oleifera',
      origin: 'India Origin',
      image: 'https://static.prod-images.emergentagent.com/jobs/2a82a0ed-6235-40ba-a9f3-10baa6fa26b5/images/7ae6f778b87f4aebf563b6f7d98b84e2c26839585b53163a0cfc51e858c4bae9.png',
      overview: 'Dried leaf, milled into a free-flowing green powder for functional foods, nutraceutical blends, beverages, bakery and supplement manufacturing.',
      grades: [
        {
          name: 'Standard Export',
          description: 'Cost-efficient bulk supply with batch COA. Specs aligned to buyer/destination requirements (UAE / UK–EU / USA / Africa)'
        },
        {
          name: 'Premium EU/USA-Ready',
          description: 'Optional heat treatment (batch-specific). Tighter microbiology targets + expanded COA panel (mycotoxins / metals / residues as required)'
        }
      ],
      keySpecs: [
        'Particle size: typical texture 0.25–0.50 mm',
        'Micro (benchmark): TVC < 5×10⁷; E. coli <10³; Salmonella absent in 25 g',
        'Mycotoxins (benchmark): Aflatoxin B1 5 µg/kg; Total 10 µg/kg; Ochratoxin 15 µg/kg'
      ],
      packaging: {
        format: 'Bulk packs with food-grade liner; kilo packs and drums available (as required)',
        storage: 'Protect from moisture/light/heat; reseal after use'
      }
    },
    {
      id: 'onion',
      name: 'Onion Powder',
      scientificName: 'Allium cepa',
      origin: 'India Origin',
      image: 'https://images.unsplash.com/photo-1737098140591-f0988ae7e15a?w=800',
      overview: 'White/off-white free-flowing powder for seasonings, sauces, snack mixes, marinades and industrial formulations.',
      grades: [
        {
          name: 'Standard Export',
          description: 'Reliable bulk supply with batch COA. Buyer/destination-aligned micro and compliance'
        },
        {
          name: 'Premium EU/USA-Ready',
          description: 'Optional steam/heat treatment. Tighter moisture and microbiology (recommended for UK–EU/USA retail/ingredient buyers)'
        }
      ],
      keySpecs: [
        'Mesh: 90% min passing 50 mesh (custom mesh on request)',
        'Moisture: ≤ 9% (premium tighter available)',
        'Total ash: ≤ 5%',
        'Acid insoluble ash: ≤ 1%'
      ],
      packaging: {
        format: '20–25 kg export packs with food-grade liner; drums/cartons available',
        shelfLife: 'Typically 18–24 months (pack/grade dependent)',
        storage: 'Cool, dry, sealed'
      }
    },
    {
      id: 'garlic',
      name: 'Garlic Powder',
      scientificName: 'Allium sativum',
      origin: 'India Origin',
      image: 'https://images.unsplash.com/photo-1710857389305-5cba9211033f?w=800',
      overview: 'Free-flowing powder for seasonings, sauces, snacks, meat analogues and industrial food manufacturing.',
      grades: [
        {
          name: 'Standard Export',
          description: 'Bulk ingredient grade with batch COA. Buyer/destination aligned micro limits'
        },
        {
          name: 'Premium EU/USA-Ready',
          description: 'Steam/heat-treated option (batch-specific). Tighter moisture + microbiology targets'
        }
      ],
      keySpecs: [
        'Mesh: 90% min passing 50 mesh',
        'Moisture: ≤ 9%',
        'Total ash: ≤ 6%',
        'Acid insoluble ash: ≤ 1%'
      ],
      packaging: {
        format: '20–25 kg lined export packs; drums on request',
        shelfLife: 'Typically 18–24 months (pack/grade dependent)',
        storage: 'Cool, dry, sealed'
      }
    },
    {
      id: 'ginger',
      name: 'Ginger Powder',
      scientificName: 'Zingiber officinale',
      origin: 'India Origin',
      image: 'https://images.unsplash.com/photo-1595414902678-862fe51c9f27?w=800',
      overview: 'Aromatic light yellow/beige powder for seasonings, bakery, beverage premixes and food manufacturing.',
      grades: [
        {
          name: 'Standard Export',
          description: 'Bulk ingredient supply with batch COA. Buyer/destination-aligned specifications'
        },
        {
          name: 'Premium EU/USA-Ready',
          description: 'Steam-treated option (batch-specific). Tighter moisture + enhanced micro targets'
        }
      ],
      keySpecs: [
        'Mesh: 90% min passing 50 mesh',
        'Moisture: ≤ 12%',
        'Total ash: ≤ 8%',
        'Acid insoluble ash: ≤ 2%',
        'Processing option: steam treated as specified'
      ],
      packaging: {
        format: '20–25 kg lined export packs; drums on request',
        shelfLife: 'Typically 18–24 months (pack/grade dependent)',
        storage: 'Cool, dry, sealed'
      }
    },
    {
      id: 'makhana-whole',
      name: 'Makhana Whole (Fox Nuts)',
      scientificName: 'Euryale ferox',
      origin: 'India Origin',
      image: 'https://images.unsplash.com/photo-1600986803572-8328914ef676?w=800',
      overview: 'Roasted & popped kernels supplied for snacking, retail packs, and ingredient use. EU market note: authorised as a traditional food from a third country in the EU.',
      grades: [
        {
          name: 'Standard Export',
          description: 'Bulk supply with size grading. COA provided; buyer/destination compliant'
        },
        {
          name: 'Premium Graded (Retail & Private Label)',
          description: 'Higher whiteness/size uniformity. Lower breakage + tighter foreign matter controls. Optional moisture-barrier packing for crispness'
        }
      ],
      keySpecs: [
        'Purity: ≥ 99%',
        'Broken kernels: ≤ 5%',
        'Foreign matter: ≤ 0.5%',
        'Moisture: controlled to pack/crispness target (varies by packing and grade)'
      ],
      packaging: {
        format: 'Bulk packs: 5 / 10 / 20 kg. Retail packs: 100 g to 1 kg (on request)',
        storage: 'Airtight; protect from humidity (crispness control)'
      }
    },
    {
      id: 'makhana-powder',
      name: 'Makhana Powder (Fox Nut Powder)',
      scientificName: 'Euryale ferox',
      origin: 'India Origin',
      image: 'https://images.unsplash.com/photo-1710421576768-ff985fa63b60?w=800',
      overview: 'Milled powder/flour from makhana kernels for bakery, snack formulations, beverage mixes, and functional blends.',
      grades: [
        {
          name: 'Standard Export',
          description: 'Bulk powder for food manufacturing. Mesh and moisture aligned to buyer application'
        },
        {
          name: 'Premium Fine Milling',
          description: 'Higher mesh (smoother mouthfeel). Tighter moisture target + stronger packaging barrier options'
        }
      ],
      keySpecs: [
        'Mesh: 60 / 80 / 100+ (custom)',
        'Moisture: controlled to buyer spec'
      ],
      packaging: {
        format: '20–25 kg lined laminated export packs; HDPE drums on request',
        shelfLife: 'Pack/grade dependent',
        storage: 'Cool, dry, sealed'
      }
    }
  ];

  return (
    <div className="w-full">
      {/* Hero */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Link to="/products" className="inline-flex items-center text-[#263776] hover:text-[#F47D20] mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Products
            </Link>
            <div className="text-center">
              <h1 className="text-5xl font-bold mb-4">Dehydrated Aromatics</h1>
              <p className="text-xl text-slate-700">
                Premium dehydrated powders and whole products from India, supplied with batch COA and destination-aligned compliance
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto space-y-20">
            {products.map((product, index) => (
              <div key={product.id} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-start ${
                index % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}>
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <img
                    src={product.image}
                    alt={product.name}
                    className="rounded-lg shadow-lg w-full object-cover h-96"
                  />
                </div>
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <h2 className="text-3xl font-bold text-[#263776] mb-2">{product.name}</h2>
                  <p className="text-slate-600 italic mb-1">{product.scientificName}</p>
                  <p className="text-sm text-[#F47D20] font-semibold mb-4">{product.origin}</p>
                  
                  <p className="text-slate-700 mb-6">{product.overview}</p>

                  {/* Grades */}
                  <Accordion type="single" collapsible className="mb-6">
                    <AccordionItem value="grades">
                      <AccordionTrigger className="text-lg font-semibold">Available Grades</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4 pt-2">
                          {product.grades.map((grade, idx) => (
                            <div key={idx} className="border-l-4 border-[#F47D20] pl-4">
                              <h4 className="font-semibold mb-1">{grade.name}</h4>
                              <p className="text-sm text-slate-600">{grade.description}</p>
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>

                  {/* Key Specs */}
                  <div className="mb-6">
                    <h3 className="font-semibold text-lg mb-3">Key Specifications</h3>
                    <ul className="space-y-2">
                      {product.keySpecs.map((spec, idx) => (
                        <li key={idx} className="flex items-start text-sm">
                          <CheckCircle2 className="w-4 h-4 text-[#263776] mr-2 flex-shrink-0 mt-0.5" />
                          <span className="text-slate-700">{spec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Packaging */}
                  <div className="bg-slate-50 p-4 rounded-lg mb-6">
                    <h3 className="font-semibold mb-2">Packaging & Storage</h3>
                    <p className="text-sm text-slate-700 mb-2"><strong>Format:</strong> {product.packaging.format}</p>
                    {product.packaging.shelfLife && (
                      <p className="text-sm text-slate-700 mb-2"><strong>Shelf Life:</strong> {product.packaging.shelfLife}</p>
                    )}
                    <p className="text-sm text-slate-700"><strong>Storage:</strong> {product.packaging.storage}</p>
                  </div>

                  <Button asChild className="bg-[#263776] text-white hover:bg-[#1e2a5e]">
                    <Link to="/contact">Request Quote for {product.name}</Link>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Premium Micro Targets */}
      <section className="py-20 bg-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="p-8">
              <h2 className="text-2xl font-bold text-[#263776] mb-4">Premium EU/USA-Ready Micro Targets (Powders)</h2>
              <p className="text-slate-700 mb-4">
                For premium powder grades targeting EU/USA markets:
              </p>
              <ul className="space-y-2 mb-4">
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-[#F47D20] mr-2 flex-shrink-0 mt-0.5" />
                  <span>Salmonella absent in 25 g</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-[#F47D20] mr-2 flex-shrink-0 mt-0.5" />
                  <span>E. coli ≤10 cfu/g</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-[#F47D20] mr-2 flex-shrink-0 mt-0.5" />
                  <span>Yeast & mould ≤1,000 cfu/g</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-[#F47D20] mr-2 flex-shrink-0 mt-0.5" />
                  <span>TPC ≤100,000 cfu/g</span>
                </li>
              </ul>
              <p className="text-sm text-slate-600">
                <strong>Standard Export Micro:</strong> Batch COA; supplied to buyer/destination requirement.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Compliance Footer */}
      <section className="py-12 bg-slate-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h3 className="text-xl font-bold mb-4">Compliance & Testing</h3>
            <p className="text-slate-300 mb-4">
              Supplied lots are tested and can be aligned to destination requirements for <strong>microbiology, contaminants and pesticide residues</strong> (Codex-aligned approach; EU contaminants framework; EU pesticide MRL framework; US preventive-controls expectations for spices). <strong>Batch COA available on request.</strong>
            </p>
            <p className="text-slate-300">
              <strong>UAE/GCC note:</strong> Gulf microbiological criteria commonly use <strong>Salmonella: absent in 25 g</strong> as a key safety criterion.
            </p>
            <div className="mt-6">
              <p className="text-sm text-slate-400">
                <strong>Documents available:</strong> COA | Specification Sheet | MSDS | Traceability | Phytosanitary / COO (as required)
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#263776] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Source Dehydrated Aromatics?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Contact us for specifications, pricing, and MOQ. We'll provide a detailed quote with compliance documentation.
          </p>
          <div className="flex gap-4 justify-center">
            <Button asChild size="lg" className="bg-[#F47D20] text-white hover:bg-[#d66b1a]">
              <Link to="/contact">Request Detailed Quote</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white bg-white/10 hover:bg-white/20">
              <Link to="/products">View All Products</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DehydratedAromatics;
